import firestoreService from "./firestoreService";
import passwordGenerator from "./passwordGenerator";
import emailService from "./emailService";
import smsService from "./smsService";

// Firebase project API key for REST API calls (doesn't affect current auth session)
const FIREBASE_API_KEY = import.meta.env.VITE_FIREBASE_API_KEY;

/**
 * AdminService handles admin-specific operations for registration management
 * Implements approval and rejection workflows with proper error handling
 */
class AdminService {
  /**
   * Approves a registration with password generation and user account creation
   * @param {string} registrationId - Registration document ID
   * @returns {Promise<Object>} - Result object with success status and generated password
   * @throws {Error} - If approval process fails
   */
  async approveRegistration(registrationId) {
    try {
      // Get registration details first
      const registration =
        await firestoreService.getRegistrationById(registrationId);
      if (!registration) {
        throw new Error("Registration not found");
      }

      if (registration.status !== "pending") {
        throw new Error(`Registration is already ${registration.status}`);
      }

      // Generate secure temporary password
      const temporaryPassword = passwordGenerator.generateSecurePassword(12);

      // Create Firebase Auth account via REST API — does NOT affect current admin session
      try {
        const res = await fetch(
          `https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=${FIREBASE_API_KEY}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email: registration.userInfo.email,
              password: temporaryPassword,
              returnSecureToken: false,
            }),
          },
        );
        const data = await res.json();
        if (!res.ok && data.error?.message !== "EMAIL_EXISTS") {
          throw new Error(
            `Failed to create auth account: ${data.error?.message}`,
          );
        }
      } catch (authError) {
        if (!authError.message?.includes("EMAIL_EXISTS")) {
          throw authError;
        }
      }

      // Perform atomic approval with user account creation in Firestore
      await firestoreService.approveRegistrationWithAccount(
        registrationId,
        temporaryPassword,
      );

      // Send approval email
      let emailResult = null;
      try {
        emailResult = await emailService.sendApprovalEmail(
          registration.userInfo.email,
          temporaryPassword,
          registration.userInfo,
        );
        console.log("✅ Approval email sent successfully");
      } catch (emailError) {
        console.error("❌ Failed to send approval email:", emailError.message);
        // Don't fail the entire approval process if email fails
        // The registration is still approved, just log the email error
      }

      // Send approval SMS and store message ID for delivery tracking
      let smsMessageId = null;
      const phone = registration.userInfo.mobileNumber;
      if (phone) {
        try {
          const loginUrl = `${window.location.origin}/admin/login`;
          const smsText = smsService.buildApprovalMessage(
            registration.userInfo.fullName,
            temporaryPassword,
            loginUrl,
            registration.userInfo,
          );
          const smsResult = await smsService.send(phone, smsText);
          if (smsResult.id) {
            smsMessageId = smsResult.id;
            // Persist SMS message ID on the registration doc for live status tracking
            await firestoreService.updateRegistrationSmsId(
              registrationId,
              smsMessageId,
            );
            console.log("✅ Approval SMS sent, id:", smsMessageId);
          }
        } catch (smsError) {
          console.error("❌ Failed to send approval SMS:", smsError.message);
          // Non-fatal — approval still succeeded
        }
      }

      return {
        success: true,
        message: "Registration approved successfully",
        temporaryPassword,
        userEmail: registration.userInfo.email,
        smsMessageId,
      };
    } catch (error) {
      console.error("Error approving registration:", error);
      throw new Error(`Failed to approve registration: ${error.message}`);
    }
  }

  /**
   * Rejects a registration with admin remark
   * @param {string} registrationId - Registration document ID
   * @param {string} remark - Admin's reason for rejection
   * @returns {Promise<Object>} - Result object with success status
   * @throws {Error} - If rejection process fails
   */
  async rejectRegistration(registrationId, remark) {
    try {
      if (!remark || !remark.trim()) {
        throw new Error("Rejection remark is required");
      }

      // Get registration details first
      const registration =
        await firestoreService.getRegistrationById(registrationId);
      if (!registration) {
        throw new Error("Registration not found");
      }

      if (registration.status !== "pending") {
        throw new Error(`Registration is already ${registration.status}`);
      }

      // Update registration status to rejected
      await firestoreService.updateRegistrationStatus(
        registrationId,
        "rejected",
        remark.trim(),
      );

      // Send rejection email
      let emailResult = null;
      try {
        emailResult = await emailService.sendRejectionEmail(
          registration.userInfo.email,
          remark.trim(),
          registration.userInfo,
        );
        console.log("✅ Rejection email sent successfully");
      } catch (emailError) {
        console.error("❌ Failed to send rejection email:", emailError.message);
        // Don't fail the entire rejection process if email fails
        // The registration is still rejected, just log the email error
      }

      return {
        success: true,
        message: "Registration rejected successfully",
        userEmail: registration.userInfo.email,
        remark: remark.trim(),
      };
    } catch (error) {
      console.error("Error rejecting registration:", error);
      throw new Error(`Failed to reject registration: ${error.message}`);
    }
  }

  /**
   * Gets registration statistics for dashboard display
   * @returns {Promise<Object>} - Statistics object with counts by status
   * @throws {Error} - If statistics retrieval fails
   */
  async getRegistrationStatistics() {
    try {
      return await firestoreService.getRegistrationStats();
    } catch (error) {
      console.error("Error fetching registration statistics:", error);
      throw new Error(`Failed to fetch statistics: ${error.message}`);
    }
  }

  /**
   * Bulk approve multiple registrations
   * @param {Array<string>} registrationIds - Array of registration IDs to approve
   * @returns {Promise<Object>} - Result object with success/failure counts
   */
  async bulkApproveRegistrations(registrationIds) {
    const results = {
      successful: [],
      failed: [],
      totalProcessed: registrationIds.length,
    };

    for (const registrationId of registrationIds) {
      try {
        const result = await this.approveRegistration(registrationId);
        results.successful.push({
          registrationId,
          userEmail: result.userEmail,
          temporaryPassword: result.temporaryPassword,
        });
      } catch (error) {
        results.failed.push({
          registrationId,
          error: error.message,
        });
      }
    }

    return results;
  }

  /**
   * Bulk reject multiple registrations
   * @param {Array<Object>} rejections - Array of {registrationId, remark} objects
   * @returns {Promise<Object>} - Result object with success/failure counts
   */
  async bulkRejectRegistrations(rejections) {
    const results = {
      successful: [],
      failed: [],
      totalProcessed: rejections.length,
    };

    for (const { registrationId, remark } of rejections) {
      try {
        const result = await this.rejectRegistration(registrationId, remark);
        results.successful.push({
          registrationId,
          userEmail: result.userEmail,
          remark: result.remark,
        });
      } catch (error) {
        results.failed.push({
          registrationId,
          error: error.message,
        });
      }
    }

    return results;
  }

  /**
   * Validates if a registration can be approved
   * @param {Object} registration - Registration object
   * @returns {Object} - Validation result with isValid and reasons
   */
  validateRegistrationForApproval(registration) {
    const issues = [];

    if (!registration) {
      issues.push("Registration not found");
      return { isValid: false, issues };
    }

    if (registration.status !== "pending") {
      issues.push(`Registration is already ${registration.status}`);
    }

    if (!registration.userInfo?.email) {
      issues.push("User email is missing");
    }

    if (!registration.userInfo?.fullName) {
      issues.push("User full name is missing");
    }

    if (!registration.receiptUrl) {
      issues.push("Payment receipt is missing");
    }

    return {
      isValid: issues.length === 0,
      issues,
    };
  }

  /**
   * Validates if a registration can be rejected
   * @param {Object} registration - Registration object
   * @param {string} remark - Rejection remark
   * @returns {Object} - Validation result with isValid and reasons
   */
  validateRegistrationForRejection(registration, remark) {
    const issues = [];

    if (!registration) {
      issues.push("Registration not found");
      return { isValid: false, issues };
    }

    if (registration.status !== "pending") {
      issues.push(`Registration is already ${registration.status}`);
    }

    if (!remark || !remark.trim()) {
      issues.push("Rejection remark is required");
    } else if (remark.trim().length < 10) {
      issues.push("Rejection remark must be at least 10 characters long");
    }

    return {
      isValid: issues.length === 0,
      issues,
    };
  }
}

export default new AdminService();
